import { useEffect, useState } from "react";
import { tryApiGet } from "../api/client";
import { useGlobalQuery } from "../api/useGlobalQuery";
import type { AuditFinding, AuditFindingsResponse } from "../api/types";
import { useAppData } from "../state/AppData";
import { NODE_AUDIT_FINDINGS_HEADING, NODE_AUDIT_NO_FINDINGS_TEXT, NODE_AUDIT_NO_ROW_TEXT, NODE_AUDIT_SECTION_DESCRIPTION, NODE_AUDIT_SECTION_TITLE, NODE_AUDIT_UNAVAILABLE_TEXT, NODE_AUDIT_VALUES_HEADING, findingBadgeInfo, nodeAuditFindingLastSeen } from '../lib/definitions/analysis';
import { colorFor } from '../lib/definitions/palette';
import { DASH, SORT_COLUMNS, clockCellForDisplay, dashTip, displayNames, findingTitle, plainFinding, headerTooltip, observerDisplayNames, type AuditPayload, type AuditRow } from "../lib/auditTable";
import { relativeTime } from "../lib/format";
import { AdvertCell, AirtimeBlock, AuditCellView, DuplicateCell, FootprintBlock, DuplicatePartners, LoopCell, PathHashCell, Dash, ScopePill, relianceValue } from "./AuditTable";
import { SectionCard } from "./SectionCard";
import { HoverTip } from "./HoverTip";

// A finding's text: a one-line lead, the possible causes as a short list, and
// the caveat behind a small "details" toggle.
function FindingText({ text, detector, verdict }: { text: string; detector: string; verdict: string }) {
  const [open, setOpen] = useState(false);
  const { lead, causes, caveat } = plainFinding(detector, verdict, text);
  return (
    <div style={{ fontSize: "0.85em" }}>
      <div>{lead}</div>
      {causes.length > 0 && (
        <ul className="node-audit-causes">
          {causes.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
      {caveat && (
        <>
          <button type="button" className="rf-link-btn node-audit-details-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            {open ? "hide details" : "details"}
          </button>
          {open && <div className="settings-field-desc">{caveat}</div>}
        </>
      )}
    </div>
  );
}

// A MeshCore node's Audit section: its row from the Audit table (read from
// the same stored snapshot the Audit tab uses, so it shares that query's
// cache) plus its findings from the stored findings snapshot
// (GET /api/audit/findings, narrowed to this node). Nothing here asks the
// backend to compute anything on request.
export function NodeAuditSection({ nodeId }: { nodeId: string }) {
  const { effectiveTheme, observers } = useAppData();
  const { data } = useGlobalQuery<AuditPayload>(["auditTable", "7d"], "/api/audit/table?window=7d&issues_only=false", 120000, true, true);
  const [findings, setFindings] = useState<AuditFinding[] | null | "unavailable">(null);

  useEffect(() => {
    let cancelled = false;
    setFindings(null);
    tryApiGet<AuditFindingsResponse>(
      `/api/audit/findings?window=7d&protocol=meshcore&native_id=${encodeURIComponent(nodeId)}`,
    )
      .then((r) => !cancelled && setFindings(r ? r.findings : "unavailable"))
      .catch(() => !cancelled && setFindings("unavailable"));
    return () => { cancelled = true; };
  }, [nodeId]);

  const row: AuditRow | undefined = data && !data.pending ? data.rows.find((r) => r.native_id === nodeId) : undefined;
  const pills = row?.region.scope_pills ?? [];
  const isInfra = row ? row.role === "repeater" || row.role === "room_server" : false;
  const cells = row
    ? ([
      ["path_hash", row.path_hash], ["duplicate", row.duplicate], ["clock_offset", clockCellForDisplay(row.clock_offset)],
      ["advert_interval", row.advert_interval], ["loop_detection", row.loop_detection], ["pkts_per_hr", row.pkts_per_hr],
    ] as const)
    : [];
  // Internal source and transport ids never reach the page: the CoreScope
  // feed, our own radio and every observer are shown by their display names.
  const names = observerDisplayNames(observers);
  const labelFor = (k: string) => SORT_COLUMNS.find((c) => c.key === k)?.label ?? k;
  // The same explanation the table's column headers carry.
  const tipped = (k: (typeof SORT_COLUMNS)[number]["key"]) => {
    const settings = data && !data.pending ? data.color_settings : null;
    if (!settings) return labelFor(k);
    return <HoverTip placement="above" text={headerTooltip(k, settings, data?.fixed_lookback_days ?? null, data?.width_lookback_days ?? 7)}>{labelFor(k)}</HoverTip>;
  };

  return (
    <SectionCard title={NODE_AUDIT_SECTION_TITLE} description={NODE_AUDIT_SECTION_DESCRIPTION}>
      <div className="page-section-title" style={{ fontSize: 11 }}>{NODE_AUDIT_VALUES_HEADING}</div>
      {!row ? (
        <div className="node-detail-note">{data?.pending ? data.message : NODE_AUDIT_NO_ROW_TEXT}</div>
      ) : (
        <dl className="node-audit-values">
          {!row.raw_role && (
            <>
              <dt>Role</dt>
              <dd>unknown — never heard advertising</dd>
            </>
          )}
          <dt>{labelFor("region")}</dt>
          <dd>
            {pills.length > 0
              ? pills.map((p, i) => <ScopePill key={`${p.tag}-${i}`} pill={p} effectiveTheme={effectiveTheme} />)
              : DASH}
          </dd>
          {isInfra && row.footprint && (
            <div className="node-audit-value-row">
              <dt>{tipped("footprint")}</dt>
              <dd><FootprintBlock row={row} effectiveTheme={effectiveTheme} /></dd>
            </div>
          )}
          {isInfra && cells.map(([key, cell]) => (
            <div key={key} className="node-audit-value-row">
              <dt>{tipped(key)}</dt>
              <dd>
                {key === "loop_detection" ? <LoopCell cell={cell} effectiveTheme={effectiveTheme} dash={row ? dashTip(row, key) : undefined} />
                  : key === "advert_interval" && data && !data.pending ? <AdvertCell cell={cell} effectiveTheme={effectiveTheme} dash={row ? dashTip(row, key) : undefined} settings={data.color_settings} />
                  : key === "duplicate" ? <DuplicateCell row={row} effectiveTheme={effectiveTheme} dash={dashTip(row, key)} />
                  : key === "path_hash" ? <PathHashCell row={row} effectiveTheme={effectiveTheme} dash={dashTip(row, key)} />
                  : <AuditCellView cell={cell} effectiveTheme={effectiveTheme} dash={row ? dashTip(row, key) : undefined} />}
                {key === "duplicate" && <DuplicatePartners row={row} />}
              </dd>
            </div>
          ))}
          {isInfra && (
            <div className="node-audit-value-row">
              <dt>{tipped("reliance")}</dt>
              <dd>{relianceValue(row) === null ? <Dash tip={dashTip(row, "reliance")} /> : `${Math.round(relianceValue(row) as number)}%`}</dd>
            </div>
          )}
          <div className="node-audit-value-row">
            <dt>Airtime</dt>
            <dd><AirtimeBlock row={row} windowLabel="7 days" /></dd>
          </div>
        </dl>
      )}

      <div className="page-section-title" style={{ fontSize: 11, marginTop: 14 }}>{NODE_AUDIT_FINDINGS_HEADING}</div>
      {findings === null && <div className="node-detail-note">Loading…</div>}
      {findings === "unavailable" && <div className="node-detail-note">{NODE_AUDIT_UNAVAILABLE_TEXT}</div>}
      {Array.isArray(findings) && findings.length === 0 && <div className="node-detail-note">{NODE_AUDIT_NO_FINDINGS_TEXT}</div>}
      {Array.isArray(findings) && findings.length > 0 && (
        <ul className="node-audit-findings">
          {findings.map((f) => {
            const level = findingBadgeInfo(f.level, f.detector, f.verdict);
            return (
              <li key={f.id}>
                <div>
                  <strong>{findingTitle(f.detector, f.detector_label, f.verdict, row ? String(row.loop_detection.value) : null)}</strong>{" "}
                  <span className="badge" style={{ background: colorFor(level.color, effectiveTheme) }}>{level.label}</span>{" "}
                  <span className="settings-field-desc">{nodeAuditFindingLastSeen(relativeTime(f.last_seen))}</span>
                </div>
                {f.level_reason && <div className="settings-field-desc">{displayNames(f.level_reason, names)}</div>}
                <FindingText text={displayNames(f.summary, names)} detector={f.detector} verdict={f.verdict} />
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}
